/**
 * =============================================================================
 * OPERATIONAL INTELLIGENCE COMPONENTS
 * =============================================================================
 * 
 * PHASE 2B: Operational Intelligence Layer
 * 
 * These components transform the dashboard from "displaying data" into
 * "surfacing operational risk and directing action."
 * 
 * 1. OperationalAlertsRail - Persistent alert system
 * 2. SLAHeatmap - Clickable risk matrix
 * 3. ExecutiveKPIs - Board-level metrics
 * 4. LiveActivityFeed - Real operational movement
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, AlertCircle, Clock, DollarSign, Shield, 
  Users, Briefcase, TrendingUp, TrendingDown, Zap, Bell,
  FileText, CheckCircle, XCircle, ChevronRight, X, Receipt,
  Calendar, Building2, Wrench, ArrowUp, ArrowDown, Eye,
  Timer, Target, Activity, BarChart3, Flame
} from 'lucide-react';
import { 
  calculateSLAStatus, 
  categorizeJobsForHeatmap, 
  getSLAMetrics,
  SLA_STATUS,
  SLA_COLORS,
  PRIORITY_CONFIG 
} from '@/config/slaConfig';

// =============================================================================
// OPERATIONAL ALERTS RAIL
// =============================================================================
export function OperationalAlertsRail({ jobs = [], contractors = [], compliance = [] }) {
  const [expanded, setExpanded] = useState(true);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const navigate = useNavigate();

  // Generate alerts from data
  const alerts = useMemo(() => {
    const alertList = [];
    const now = new Date();

    // SLA Alerts
    const slaMetrics = getSLAMetrics(jobs);
    if (slaMetrics.breached > 0) {
      alertList.push({
        id: 'sla_breached',
        type: 'critical',
        category: 'SLA',
        title: `${slaMetrics.breached} SLA ${slaMetrics.breached === 1 ? 'Breach' : 'Breaches'}`,
        description: `${slaMetrics.avgHoursOverdue}h avg overdue`,
        icon: AlertTriangle,
        action: () => navigate('/phs/helpdesk?filter=sla_breached'),
        count: slaMetrics.breached,
      });
    }
    if (slaMetrics.risk > 0) {
      alertList.push({
        id: 'sla_risk',
        type: 'warning',
        category: 'SLA',
        title: `${slaMetrics.risk} Jobs at Risk`,
        description: 'Approaching SLA breach',
        icon: Clock,
        action: () => navigate('/phs/helpdesk?filter=sla_risk'),
        count: slaMetrics.risk,
      });
    }

    // Unassigned jobs
    const unassigned = jobs.filter(j => !j.contractor_id && !['Completed', 'Cancelled', 'Closed'].includes(j.status));
    if (unassigned.length > 0) {
      alertList.push({
        id: 'unassigned',
        type: 'warning',
        category: 'SLA',
        title: `${unassigned.length} Unassigned ${unassigned.length === 1 ? 'Job' : 'Jobs'}`,
        description: 'No contractor allocated',
        icon: Users,
        action: () => navigate('/phs/helpdesk?filter=unassigned'),
        count: unassigned.length,
      });
    }

    // Finance Alerts
    const awaitingApproval = jobs.filter(j => 
      j.status?.toLowerCase().includes('awaiting') && 
      (j.status?.toLowerCase().includes('quote') || j.status?.toLowerCase().includes('approval'))
    );
    if (awaitingApproval.length > 0) {
      alertList.push({
        id: 'awaiting_approval',
        type: 'info',
        category: 'Finance',
        title: `${awaitingApproval.length} Awaiting Approval`,
        description: 'Quotes need review',
        icon: FileText,
        action: () => navigate('/phs/helpdesk?status=Quote%20Awaiting%20Review'),
        count: awaitingApproval.length,
      });
    }

    const missingEvidence = jobs.filter(j => 
      j.status === 'Completed' && 
      (!j.evidence?.photos?.length && !j.evidence?.documents?.length)
    );
    if (missingEvidence.length > 0) {
      alertList.push({
        id: 'missing_evidence',
        type: 'warning',
        category: 'Finance',
        title: `${missingEvidence.length} Missing Evidence`,
        description: 'Completed jobs without photos',
        icon: Eye,
        action: () => navigate('/phs/helpdesk?filter=missing_evidence'),
        count: missingEvidence.length,
      });
    }

    const invoiceOverQuote = jobs.filter(j => {
      const quoteValue = j.quotes?.[0]?.total_inc_vat || j.approved_value || 0;
      const invoiceValue = j.invoice?.total || 0;
      return invoiceValue > 0 && quoteValue > 0 && invoiceValue > quoteValue * 1.1;
    });
    if (invoiceOverQuote.length > 0) {
      alertList.push({
        id: 'invoice_variance',
        type: 'warning',
        category: 'Finance',
        title: `${invoiceOverQuote.length} Invoice ${invoiceOverQuote.length === 1 ? 'Variance' : 'Variances'}`,
        description: '>10% over approved quote',
        icon: DollarSign,
        action: () => navigate('/phs/finance?view=invoice-review&filter=variance'),
        count: invoiceOverQuote.length,
      });
    }

    // Compliance Alerts (if data provided)
    const expiredCerts = compliance.filter(c => c.expiry_date && new Date(c.expiry_date) < now);
    if (expiredCerts.length > 0) {
      alertList.push({
        id: 'expired_compliance',
        type: 'critical',
        category: 'Compliance',
        title: `${expiredCerts.length} Expired ${expiredCerts.length === 1 ? 'Certificate' : 'Certificates'}`,
        description: 'Immediate attention required',
        icon: Shield,
        action: () => navigate('/phs/assets?tab=compliance&status=expired'),
        count: expiredCerts.length,
      });
    }

    const expiringCerts = compliance.filter(c => {
      if (!c.expiry_date) return false;
      const expiry = new Date(c.expiry_date);
      return expiry > now && (expiry - now) / 86400000 <= 30;
    });
    if (expiringCerts.length > 0) {
      alertList.push({
        id: 'expiring_compliance',
        type: 'warning',
        category: 'Compliance',
        title: `${expiringCerts.length} Expiring Soon`,
        description: 'Within 30 days',
        icon: Calendar,
        action: () => navigate('/phs/assets?tab=compliance&status=expiring'),
        count: expiringCerts.length,
      });
    }

    // Operations Alerts
    const highValue = jobs.filter(j => (j.approved_value || j.quote_value || 0) > 5000);
    const ageingJobs = jobs.filter(j => {
      const created = new Date(j.created_at);
      return (now - created) / 86400000 > 14 && !['Completed', 'Cancelled', 'Closed', 'Paid'].includes(j.status);
    });
    if (ageingJobs.length > 0) {
      alertList.push({
        id: 'ageing_jobs',
        type: 'info',
        category: 'Operations',
        title: `${ageingJobs.length} Ageing ${ageingJobs.length === 1 ? 'Job' : 'Jobs'}`,
        description: 'Open >14 days',
        icon: Clock,
        action: () => navigate('/phs/helpdesk?filter=ageing'),
        count: ageingJobs.length,
      });
    }

    const noUpdate = jobs.filter(j => {
      const lastUpdate = new Date(j.updated_at || j.created_at);
      return (now - lastUpdate) / 86400000 > 7 && !['Completed', 'Cancelled', 'Closed', 'Paid'].includes(j.status);
    });
    if (noUpdate.length > 5) {
      alertList.push({
        id: 'stalled_jobs',
        type: 'warning',
        category: 'Operations',
        title: `${noUpdate.length} Stalled Jobs`,
        description: 'No update in 7+ days',
        icon: AlertCircle,
        action: () => navigate('/phs/helpdesk?filter=stalled'),
        count: noUpdate.length,
      });
    }

    // Contractor Alerts
    const poorPerformers = contractors.filter(c => (c.sla_score || 100) < 70);
    if (poorPerformers.length > 0) {
      alertList.push({
        id: 'contractor_performance',
        type: 'warning',
        category: 'Contractors',
        title: `${poorPerformers.length} Poor Performers`,
        description: 'SLA score <70%',
        icon: Users,
        action: () => navigate('/phs/contractors?tab=performance&score=poor'),
        count: poorPerformers.length,
      });
    }

    return alertList.filter(a => !dismissedAlerts.has(a.id));
  }, [jobs, contractors, compliance, dismissedAlerts, navigate]);

  // Group alerts by category
  const groupedAlerts = useMemo(() => {
    const groups = {};
    alerts.forEach(alert => {
      if (!groups[alert.category]) groups[alert.category] = [];
      groups[alert.category].push(alert);
    });
    return groups;
  }, [alerts]);

  const criticalCount = alerts.filter(a => a.type === 'critical').length;
  const warningCount = alerts.filter(a => a.type === 'warning').length;

  if (alerts.length === 0) return null;

  const typeColors = {
    critical: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', badge: 'bg-red-100 text-red-700' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' },
  };

  return (
    <div 
      className={`mb-6 rounded-xl border-2 overflow-hidden transition-all ${
        criticalCount > 0 ? 'border-red-300 bg-red-50/50' : 'border-amber-200 bg-amber-50/30'
      }`}
      data-testid="operational-alerts-rail"
    >
      {/* Header */}
      <div 
        className={`px-4 py-3 flex items-center justify-between cursor-pointer ${
          criticalCount > 0 ? 'bg-red-100' : 'bg-amber-100'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded-lg ${criticalCount > 0 ? 'bg-red-200' : 'bg-amber-200'}`}>
            <Bell className={`w-4 h-4 ${criticalCount > 0 ? 'text-red-700' : 'text-amber-700'}`} />
          </div>
          <div>
            <h3 className={`text-sm font-bold ${criticalCount > 0 ? 'text-red-800' : 'text-amber-800'}`}>
              Operational Alerts
            </h3>
            <p className="text-xs text-slate-600">
              {alerts.length} {alerts.length === 1 ? 'item' : 'items'} requiring attention
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full">
              {criticalCount} Critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full">
              {warningCount} Warning
            </span>
          )}
          <ChevronRight className={`w-4 h-4 text-slate-400 transition ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </div>

      {/* Alert Grid */}
      {expanded && (
        <div className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {alerts.map(alert => {
              const Icon = alert.icon;
              const colors = typeColors[alert.type];
              return (
                <div
                  key={alert.id}
                  className={`relative p-3 rounded-xl ${colors.bg} border ${colors.border} hover:shadow-md transition text-left group`}
                  data-testid={`alert-${alert.id}`}
                >
                  {/* Dismiss action */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setDismissedAlerts(prev => new Set([...prev, alert.id]));
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setDismissedAlerts(prev => new Set([...prev, alert.id]))}
                    className="absolute top-1 right-1 p-1 opacity-0 group-hover:opacity-100 hover:bg-white/50 rounded transition cursor-pointer"
                    aria-label="Dismiss alert"
                  >
                    <X className="w-3 h-3 text-slate-400" />
                  </div>
                  
                  {/* Main clickable area */}
                  <div 
                    onClick={alert.action}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && alert.action()}
                    className="cursor-pointer"
                  >
                    <div className="flex items-start gap-2">
                      <div className={`p-1.5 rounded-lg ${colors.badge.split(' ')[0]}`}>
                        <Icon className={`w-4 h-4 ${colors.icon}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-lg font-bold text-slate-800">{alert.count}</span>
                        </div>
                        <p className="text-xs font-medium text-slate-700 truncate">{alert.title}</p>
                        <p className="text-[10px] text-slate-500 truncate">{alert.description}</p>
                      </div>
                    </div>
                    
                    {/* Category badge */}
                    <div className="mt-2">
                      <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${colors.badge}`}>
                        {alert.category}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SLA HEATMAP - Clickable Risk Matrix
// =============================================================================
export function SLAHeatmap({ jobs = [] }) {
  const navigate = useNavigate();

  const matrix = useMemo(() => categorizeJobsForHeatmap(jobs), [jobs]);

  const priorities = [
    { key: 'emergency', label: 'Emergency' },
    { key: 'urgent', label: 'Urgent' },
    { key: 'high', label: 'High' },
    { key: 'medium', label: 'Medium' },
    { key: 'standard', label: 'Standard' },
  ];

  const statuses = [
    { key: SLA_STATUS.HEALTHY, label: 'Healthy' },
    { key: SLA_STATUS.WARNING, label: 'Warning' },
    { key: SLA_STATUS.RISK, label: 'At Risk' },
    { key: SLA_STATUS.BREACHED, label: 'Breached' },
  ];

  const handleCellClick = (priority, status) => {
    const params = new URLSearchParams();
    params.set('priority', priority);
    params.set('sla', status);
    navigate(`/phs/helpdesk?${params.toString()}`);
  };

  // Get total for each status column
  const statusTotals = {};
  statuses.forEach(s => {
    statusTotals[s.key] = priorities.reduce((sum, p) => sum + (matrix[p.key]?.[s.key]?.length || 0), 0);
  });

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="sla-heatmap">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800">SLA Risk Matrix</h3>
          <p className="text-xs text-slate-500">Click any cell to view jobs</p>
        </div>
        <div className="flex items-center gap-2">
          {Object.entries(statusTotals).map(([status, count]) => {
            if (count === 0) return null;
            const colors = SLA_COLORS[status];
            return (
              <span 
                key={status} 
                className="px-2 py-0.5 rounded text-xs font-semibold"
                style={{ backgroundColor: colors.bg, color: colors.text }}
              >
                {count}
              </span>
            );
          })}
        </div>
      </div>

      <div className="p-4 overflow-x-auto">
        <table className="w-full min-w-[500px]">
          <thead>
            <tr>
              <th className="text-left text-xs font-medium text-slate-500 pb-2 w-24">Priority</th>
              {statuses.map(s => (
                <th 
                  key={s.key} 
                  className="text-center text-xs font-medium pb-2 px-2"
                  style={{ color: SLA_COLORS[s.key].text }}
                >
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {priorities.map(p => {
              const config = PRIORITY_CONFIG[p.key] || PRIORITY_CONFIG.medium;
              return (
                <tr key={p.key}>
                  <td className="py-1.5">
                    <span 
                      className="text-xs font-medium px-2 py-1 rounded"
                      style={{ backgroundColor: config.bg, color: config.color }}
                    >
                      {config.label}
                    </span>
                  </td>
                  {statuses.map(s => {
                    const count = matrix[p.key]?.[s.key]?.length || 0;
                    const colors = SLA_COLORS[s.key];
                    const isClickable = count > 0;
                    
                    return (
                      <td key={s.key} className="px-1 py-1.5">
                        <button
                          onClick={() => isClickable && handleCellClick(p.key, s.key)}
                          disabled={!isClickable}
                          className={`w-full h-10 rounded-lg text-sm font-bold transition ${
                            isClickable 
                              ? 'hover:shadow-md hover:scale-105 cursor-pointer' 
                              : 'cursor-default opacity-50'
                          }`}
                          style={{ 
                            backgroundColor: count > 0 ? colors.bg : '#F9FAFB',
                            color: count > 0 ? colors.text : '#9CA3AF',
                            border: `1px solid ${count > 0 ? colors.border : '#E5E7EB'}`
                          }}
                        >
                          {count}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// EXECUTIVE KPIS - Board-Level Metrics
// =============================================================================
export function ExecutiveKPIs({ jobs = [], previousPeriodJobs = [] }) {
  const metrics = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const activeJobs = jobs.filter(j => !['Completed', 'Cancelled', 'Closed', 'Paid'].includes(j.status));
    const completedThisMonth = jobs.filter(j => {
      const completed = j.completed_at ? new Date(j.completed_at) : null;
      return completed && completed >= startOfMonth && j.status === 'Completed';
    });
    
    // SLA metrics
    const slaMetrics = getSLAMetrics(jobs);
    
    // Financial metrics
    const openJobValue = activeJobs.reduce((sum, j) => sum + (j.approved_value || j.quote_value || 0), 0);
    const awaitingPayment = jobs
      .filter(j => ['Invoice Submitted', 'Invoice Approved', 'Invoiced'].includes(j.status))
      .reduce((sum, j) => sum + (j.invoice?.total || j.approved_value || 0), 0);
    
    // Quote variance
    const jobsWithVariance = jobs.filter(j => {
      const quoteVal = j.quotes?.[0]?.total_inc_vat || 0;
      const invoiceVal = j.invoice?.total || 0;
      return quoteVal > 0 && invoiceVal > 0;
    });
    const avgVariance = jobsWithVariance.length > 0 
      ? jobsWithVariance.reduce((sum, j) => {
          const quoteVal = j.quotes?.[0]?.total_inc_vat || 0;
          const invoiceVal = j.invoice?.total || 0;
          return sum + ((invoiceVal - quoteVal) / quoteVal * 100);
        }, 0) / jobsWithVariance.length
      : 0;
    
    // Completion cycle
    const completedWithDates = jobs.filter(j => j.completed_at && j.created_at);
    const avgCycleDays = completedWithDates.length > 0
      ? completedWithDates.reduce((sum, j) => {
          return sum + (new Date(j.completed_at) - new Date(j.created_at)) / 86400000;
        }, 0) / completedWithDates.length
      : 0;

    return {
      activeRisk: slaMetrics.atRiskCount,
      slaCompliance: slaMetrics.slaCompliance,
      openJobValue,
      awaitingPayment,
      avgVariance: avgVariance.toFixed(1),
      avgCycleDays: avgCycleDays.toFixed(1),
      completedThisMonth: completedThisMonth.length,
      activeJobs: activeJobs.length,
    };
  }, [jobs]);

  const kpis = [
    {
      label: 'Active Operational Risk',
      value: metrics.activeRisk,
      suffix: '',
      trend: metrics.activeRisk > 5 ? 'down' : 'up',
      trendLabel: metrics.activeRisk > 5 ? 'High risk' : 'Under control',
      icon: AlertTriangle,
      color: metrics.activeRisk > 10 ? '#DC2626' : metrics.activeRisk > 5 ? '#F59E0B' : '#22C55E',
      critical: metrics.activeRisk > 10,
    },
    {
      label: 'SLA Compliance',
      value: metrics.slaCompliance,
      suffix: '%',
      trend: parseFloat(metrics.slaCompliance) >= 85 ? 'up' : 'down',
      trendLabel: parseFloat(metrics.slaCompliance) >= 85 ? 'On target' : 'Below target',
      icon: Target,
      color: parseFloat(metrics.slaCompliance) >= 85 ? '#22C55E' : '#F59E0B',
    },
    {
      label: 'Open Job Value',
      value: `£${(metrics.openJobValue / 1000).toFixed(0)}k`,
      suffix: '',
      trend: 'neutral',
      trendLabel: `${metrics.activeJobs} active jobs`,
      icon: Briefcase,
      color: '#3B82F6',
    },
    {
      label: 'Outstanding Payments',
      value: `£${(metrics.awaitingPayment / 1000).toFixed(0)}k`,
      suffix: '',
      trend: metrics.awaitingPayment > 50000 ? 'down' : 'up',
      trendLabel: 'Awaiting settlement',
      icon: DollarSign,
      color: metrics.awaitingPayment > 50000 ? '#F59E0B' : '#22C55E',
    },
    {
      label: 'Quote Variance',
      value: metrics.avgVariance,
      suffix: '%',
      trend: parseFloat(metrics.avgVariance) > 5 ? 'down' : 'up',
      trendLabel: parseFloat(metrics.avgVariance) > 5 ? 'Above target' : 'Within tolerance',
      icon: TrendingUp,
      color: parseFloat(metrics.avgVariance) > 10 ? '#DC2626' : parseFloat(metrics.avgVariance) > 5 ? '#F59E0B' : '#22C55E',
    },
    {
      label: 'Avg Completion Cycle',
      value: metrics.avgCycleDays,
      suffix: ' days',
      trend: parseFloat(metrics.avgCycleDays) < 7 ? 'up' : 'neutral',
      trendLabel: parseFloat(metrics.avgCycleDays) < 7 ? 'Fast turnaround' : 'Standard pace',
      icon: Timer,
      color: parseFloat(metrics.avgCycleDays) < 7 ? '#22C55E' : '#3B82F6',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="executive-kpis">
      {kpis.map((kpi, idx) => {
        const Icon = kpi.icon;
        const TrendIcon = kpi.trend === 'up' ? ArrowUp : kpi.trend === 'down' ? ArrowDown : Activity;
        
        return (
          <div 
            key={idx}
            className={`bg-white rounded-xl border p-4 ${
              kpi.critical ? 'border-red-300 ring-2 ring-red-100' : 'border-slate-200'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div 
                className="p-2 rounded-lg"
                style={{ backgroundColor: `${kpi.color}15` }}
              >
                <Icon className="w-4 h-4" style={{ color: kpi.color }} />
              </div>
              <div className={`flex items-center gap-0.5 text-xs ${
                kpi.trend === 'up' ? 'text-green-600' : 
                kpi.trend === 'down' ? 'text-red-600' : 'text-slate-500'
              }`}>
                <TrendIcon className="w-3 h-3" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-800">
              {kpi.value}{kpi.suffix}
            </p>
            <p className="text-xs font-medium text-slate-600 mt-1">{kpi.label}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{kpi.trendLabel}</p>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// LIVE ACTIVITY FEED - Real Operational Movement
// =============================================================================
export function LiveActivityFeed({ jobs = [], limit = 8 }) {
  const navigate = useNavigate();

  const activities = useMemo(() => {
    const events = [];
    const now = new Date();

    // Generate activities from jobs
    jobs.forEach(job => {
      // Quote submitted
      job.quotes?.forEach(q => {
        if (q.submitted_at) {
          events.push({
            id: `quote-${job.id}-${q.submitted_at}`,
            type: 'quote_submitted',
            title: 'Quote Submitted',
            description: `£${(q.total_inc_vat || 0).toLocaleString()} by ${q.submitted_by || job.contractor_name || 'Contractor'}`,
            job_ref: job.job_reference,
            job_id: job.id,
            timestamp: new Date(q.submitted_at),
            icon: FileText,
            color: '#F59E0B',
          });
        }
        if (q.approved_at) {
          events.push({
            id: `approved-${job.id}-${q.approved_at}`,
            type: 'quote_approved',
            title: 'Quote Approved',
            description: `£${(q.total_inc_vat || 0).toLocaleString()} approved`,
            job_ref: job.job_reference,
            job_id: job.id,
            timestamp: new Date(q.approved_at),
            icon: CheckCircle,
            color: '#22C55E',
          });
        }
      });

      // Invoice events
      if (job.invoice?.submitted_at) {
        events.push({
          id: `inv-sub-${job.id}`,
          type: 'invoice_submitted',
          title: 'Invoice Submitted',
          description: `£${(job.invoice.total || 0).toLocaleString()} from ${job.contractor_name || 'Contractor'}`,
          job_ref: job.job_reference,
          job_id: job.id,
          timestamp: new Date(job.invoice.submitted_at),
          icon: Receipt,
          color: '#6366F1',
        });
      }

      // Job creation (recent only)
      const created = new Date(job.created_at);
      if ((now - created) < 7 * 24 * 60 * 60 * 1000) { // Within last 7 days
        events.push({
          id: `created-${job.id}`,
          type: 'job_created',
          title: job.priority?.toLowerCase() === 'emergency' ? 'Emergency Job Logged' : 'New Job Logged',
          description: `${job.category || 'General'} at ${job.site_name || 'Unknown site'}`,
          job_ref: job.job_reference,
          job_id: job.id,
          timestamp: created,
          icon: Briefcase,
          color: job.priority?.toLowerCase() === 'emergency' ? '#DC2626' : '#0D9488',
          priority: job.priority?.toLowerCase() === 'emergency',
        });
      }

      // Contractor assigned
      if (job.contractor_assigned_at) {
        events.push({
          id: `assigned-${job.id}`,
          type: 'contractor_assigned',
          title: 'Contractor Assigned',
          description: `${job.contractor_name || 'Unknown'} assigned`,
          job_ref: job.job_reference,
          job_id: job.id,
          timestamp: new Date(job.contractor_assigned_at),
          icon: Users,
          color: '#3B82F6',
        });
      }

      // Completion
      if (job.completed_at) {
        events.push({
          id: `completed-${job.id}`,
          type: 'job_completed',
          title: 'Job Completed',
          description: `${job.category || 'General'} - ${job.site_name || 'Unknown'}`,
          job_ref: job.job_reference,
          job_id: job.id,
          timestamp: new Date(job.completed_at),
          icon: CheckCircle,
          color: '#22C55E',
        });
      }
    });

    // Sort by timestamp and limit
    return events
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, limit);
  }, [jobs, limit]);

  const formatTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="live-activity-feed">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Activity className="w-4 h-4 text-teal-600" />
            <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          </div>
          <h3 className="font-semibold text-slate-800">Live Activity</h3>
        </div>
        <span className="text-xs text-slate-500">{activities.length} recent</span>
      </div>

      <div className="divide-y divide-slate-50">
        {activities.length === 0 ? (
          <div className="p-6 text-center">
            <Activity className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No recent activity</p>
          </div>
        ) : (
          activities.map(activity => {
            const Icon = activity.icon;
            return (
              <button
                key={activity.id}
                onClick={() => navigate(`/phs/helpdesk/job/${activity.job_id}`)}
                className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition text-left"
              >
                <div 
                  className="p-1.5 rounded-lg flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: `${activity.color}15` }}
                >
                  <Icon className="w-3.5 h-3.5" style={{ color: activity.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-800">{activity.title}</p>
                    {activity.priority && (
                      <Flame className="w-3 h-3 text-red-500" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{activity.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-teal-600 font-medium">{activity.job_ref}</span>
                    <span className="text-[10px] text-slate-400">{formatTime(activity.timestamp)}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export default {
  OperationalAlertsRail,
  SLAHeatmap,
  ExecutiveKPIs,
  LiveActivityFeed,
};
